import React, {  useEffect, Fragment } from 'react';
import styled from 'styled-components/macro';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import Page, { Back, Title } from '../../../components/Page';
import Card from '../../../components/Card';
import { Table, Thead, Tr, Th, Tbody, Td } from '../../../components/Table';
import { LoadingOverlay } from '../../../components/Loading';
import { State } from '../reducer';
import { formatDate } from '../../../helpers/formatters';
import { distances } from '../../../styles/constants';

interface CampaignsCompletedProps {
    campaignsCompletedState: State;
    fetchDiscounts: (accountId: string) => void;
    fetchCampaigns: (accountId: string) => void;
}

const CampaignsCompleted = (
    {
        campaignsCompletedState,
        fetchDiscounts,
        fetchCampaigns,
    }: CampaignsCompletedProps
) => {
    const { accountId, campaignId } = useParams();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (accountId) {
            fetchDiscounts(accountId);
            fetchCampaigns(accountId);
        }
    }, [accountId, fetchDiscounts, fetchCampaigns]);

    const { discounts, campaigns } = campaignsCompletedState;

    const currentDate = new Date().getTime();
    // eslint-disable-next-line array-callback-return
    const completedDiscounts = discounts.filter(discount => {
        const toDate = new Date(discount.requirement.purchase_to).getTime();
        if (currentDate > toDate && discount.campaign_id === campaignId) {
            return discount;
        }
    });

    const campaign = campaigns.find(campaign => campaign.campaign_id === campaignId);

    if (!campaigns || !campaign || discounts.length === 0) {
        return <LoadingOverlay />;
    }

    return (
        <Page title={
            <Fragment>
                <Back />
                <Title>{t('marketing.campaign.campaigns_completed')}</Title>
            </Fragment>
        }>
            <Card
                title={t('marketing.campaign.completed')}
                subtitle={t('marketing.campaign.discounts_completed_text', { name: campaign.name })}
                shortHr
                marginBottom={distances.small}
                id="completed"
            >
                <Table>
                    <Thead>
                        <Tr>
                            <StyledTh>{t('marketing.campaign.name')}</StyledTh>
                            <Th>{t('marketing.campaign.time_used')}</Th>
                            <Th>{t('marketing.campaign.discount_given')}</Th>
                            <Th>{t('marketing.campaign.stamps_used')}</Th>
                            <Th>{t('marketing.campaign.started')}</Th>
                            <Th>{t('marketing.campaign.ended')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {completedDiscounts.map((discount, index) => (
                            <Tr key={index}>
                                <StyledTd>{discount.name}</StyledTd>
                                <Td>{discount.statistics?.used ?? 0}</Td>
                                <Td>{discount.statistics?.amount ?? 0}</Td>
                                <Td>{discount.statistics?.stamp ?? 0}</Td>
                                <Td>
                                    {formatDate(
                                        new Date(discount.requirement.purchase_from).toISOString(),
                                        i18n.language
                                    )}
                                </Td>
                                <Td>
                                    {formatDate(
                                        new Date(discount.requirement.purchase_to).toISOString(),
                                        i18n.language
                                    )}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Card>
        </Page>
    );
};

const StyledTh = styled(Th as any)`
    width: 25%;
`;

const StyledTd = styled(Td as any)`
    width: 25%;
`;

export default CampaignsCompleted;
